import React, { useEffect, useState } from 'react';
import { Outlet, useLocation, useNavigate } from 'react-router-dom';

import { useLocalStorageState, useUser } from 'hooks';
import { Spinner } from '../Spinner';
import { IUserContext } from 'context';
import { ApiUser, Coupon } from 'types';
import { useAppSelector } from 'store';
import { selectUser } from 'store/user';
import { COUPON, SECOND_ACCOUNT_PAYMENT_REQUIRED } from 'constants/index';
import { getIsAdminByRoles } from 'utils';

export function RequireAuth({ children }: { children: JSX.Element }) {
  const navigate = useNavigate();
  const location = useLocation();
  const user: ApiUser | null = useAppSelector(selectUser);
  const { isLoading }: IUserContext = useUser();
  const [coupon] = useLocalStorageState<Coupon | null>(COUPON, null);

  const [allowChildren, setAllowChildren] = useState<boolean>(false);

  const isMember = user?.clinicUser?.via3UserType === 'Member';

  useEffect(() => {
    const secondAccountPaymentRequired = localStorage.getItem(SECOND_ACCOUNT_PAYMENT_REQUIRED) === 'true';

    if (!isLoading) {
      if (!user) {
        window.open(process.env.REACT_APP_HOME_PAGE_FOR_NON_AUTH_URL, '_self');
        return;
      }

      const subscriptionPlanCode = user.clinicUser?.subscriptionInfo?.planCode;
      if (secondAccountPaymentRequired && subscriptionPlanCode && subscriptionPlanCode !== 'TRIAL') {
        localStorage.removeItem(SECOND_ACCOUNT_PAYMENT_REQUIRED);
      }

      const isUnpaidSecondAccountCandidate =
        getIsAdminByRoles(user.clinicUser.roles || []) &&
        user.clinicUser.createRoomSkipped &&
        !user.clinicUser.subscriptionInfo &&
        user.clinicUser.via3UserType !== 'Member';
      const shouldEnforceSecondAccountFlow =
        localStorage.getItem(SECOND_ACCOUNT_PAYMENT_REQUIRED) === 'true' && isUnpaidSecondAccountCandidate;

      if (localStorage.getItem(SECOND_ACCOUNT_PAYMENT_REQUIRED) === 'true' && !isUnpaidSecondAccountCandidate) {
        localStorage.removeItem(SECOND_ACCOUNT_PAYMENT_REQUIRED);
      }

      if (shouldEnforceSecondAccountFlow || isUnpaidSecondAccountCandidate) {
        if (location.pathname === '/registerSecondAccount') {
          setAllowChildren(true);
          return;
        }
        navigate('/registerSecondAccount');
        return;
      }

      if (!isMember && (!user!.clinicUser.createRoomSkipped || coupon || !user.clinicUser.subscriptionInfo)) {
        navigate('register');
        return;
      }

      setAllowChildren(true);
    }
  }, [user, isLoading, coupon, isMember, location.pathname, navigate]);

  if (!allowChildren) return <Spinner />;
  return children ?? <Outlet />;
}
